// src/services/studyPlanProgressService.js
// Service to calculate study plan progress from actual study plan JSON files

import { auth } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Fetch study plan from JSON file
 */
const fetchStudyPlan = async (provider, level) => {
  const examKey = `${provider.toLowerCase()}-${level.toLowerCase()}`;
  try {
    const response = await fetch(`/data/lessons/study-plans/${examKey}-study-plan.json`);
    if (!response.ok) {
      console.warn(`Study plan not found for ${examKey}`);
      return null;
    }
    return await response.json();
  } catch (error) {
    console.error(`Failed to fetch study plan for ${examKey}:`, error);
    return null;
  }
};

/**
 * Get user's exam provider and level from localStorage or preferences
 */
const getUserExamData = () => {
  try {
    const userId = auth?.currentUser?.uid;
    if (userId) {
      // Try user-specific storage first
      const userExamData = localStorage.getItem(`selectedExamData_${userId}`);
      if (userExamData) {
        const parsed = JSON.parse(userExamData);
        return {
          examProvider: parsed.examProvider || 'TELC',
          examLevel: parsed.level || 'B2'
        };
      }
    }

    // Fallback to session storage
    const savedData = sessionStorage.getItem('selectedExamData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      return {
        examProvider: parsed.examProvider || 'TELC',
        examLevel: parsed.level || 'B2'
      };
    }

    // Fallback to general localStorage
    const generalData = localStorage.getItem('selectedExamData');
    if (generalData) {
      const parsed = JSON.parse(generalData);
      return {
        examProvider: parsed.examProvider || 'TELC',
        examLevel: parsed.level || 'B2'
      };
    }
  } catch (error) {
    console.error('Error loading exam data:', error);
  }

  // Default fallback
  return {
    examProvider: 'TELC',
    examLevel: 'B2'
  };
};

/**
 * Calculate study plan progress from actual study plan JSON
 * @param {Array} completedLessons - Array of completed lesson IDs
 * @param {string} examProvider - Exam provider (e.g., 'TELC', 'Goethe', 'ÖSD')
 * @param {string} examLevel - Exam level (e.g., 'B1', 'B2', 'C1')
 * @returns {Promise<Object>} Progress object with completed, total, estimatedTime
 */
export const calculateStudyPlanProgress = async (completedLessons = [], examProvider = null, examLevel = null) => {
  try {
    // Get exam data if not provided
    if (!examProvider || !examLevel) {
      const examData = getUserExamData();
      examProvider = examProvider || examData.examProvider;
      examLevel = examLevel || examData.examLevel;
    }

    // Fetch the study plan
    const studyPlan = await fetchStudyPlan(examProvider, examLevel);
    
    if (!studyPlan || !studyPlan.modules) {
      console.warn('No study plan found, returning default progress');
      return {
        completed: completedLessons.length || 0,
        total: 32, // Fallback default
        estimatedTime: 12, // Fallback default
        examProvider,
        examLevel
      };
    }

    // Calculate progress from actual study plan
    const completedSet = new Set(completedLessons);
    let completedCount = 0;
    let totalDuration = 0;
    let completedDuration = 0;

    const allLessons = studyPlan.modules.flatMap(m => m.lessons || []);

    allLessons.forEach(lesson => {
      // Only count non-video lessons for progress
      if (lesson.type === 'video') {
        return;
      }

      totalDuration += lesson.duration || 0;

      if (completedSet.has(lesson.lessonId)) {
        completedCount++;
        completedDuration += lesson.duration || 0;
      }
    });

    const totalLessons = allLessons.filter(l => l.type !== 'video').length;
    const estimatedTime = Math.round((totalDuration - completedDuration) / 60);

    return {
      completed: completedCount,
      total: totalLessons || 32,
      estimatedTime: estimatedTime || 12,
      examProvider,
      examLevel,
      studyPlanTitle: studyPlan.title || `${examProvider} ${examLevel} Study Plan`
    };
  } catch (error) {
    console.error('Error calculating study plan progress:', error);
    return {
      completed: completedLessons.length || 0,
      total: 32,
      estimatedTime: 12,
      examProvider: examProvider || 'TELC',
      examLevel: examLevel || 'B2',
      error: error.message
    };
  }
};

/**
 * Extract completed lesson IDs from study sessions
 */
const extractCompletedLessonsFromSessions = (sessions = []) => {
  const completedLessons = new Set();
  
  sessions.forEach(session => {
    // If session is completed and has an activityId (lesson ID), add it
    if (session.completed && session.activityId && session.type === 'lesson') {
      completedLessons.add(session.activityId);
    }
  });
  
  return Array.from(completedLessons);
};

/**
 * Fetch completed lessons from Firestore study sessions
 */
const fetchCompletedLessonsFromFirestore = async (userId) => {
  try {
    if (!userId) return [];
    
    const sessionsRef = collection(db, 'users', userId, 'studySessions');
    const q = query(
      sessionsRef,
      where('completed', '==', true),
      where('type', '==', 'lesson'),
      orderBy('endTime', 'desc'),
      limit(500) // Get up to 500 completed lessons
    );
    
    const querySnapshot = await getDocs(q);
    const completedLessons = new Set();
    
    querySnapshot.forEach((doc) => {
      const session = doc.data();
      if (session.activityId) {
        completedLessons.add(session.activityId);
      }
    });
    
    return Array.from(completedLessons);
  } catch (error) {
    console.error('Error fetching completed lessons from Firestore:', error);
    return [];
  }
};

/**
 * Get study plan progress for dashboard
 * This is the main function to use in dashboard components
 * @param {Object} userStats - User stats object (optional)
 * @param {string} userId - User ID (optional, will use auth.currentUser if not provided)
 */
export const getStudyPlanProgressForDashboard = async (userStats = null, userId = null) => {
  try {
    // Try multiple sources for completed lessons
    let completedLessons = [];
    
    // 1. Check if completedLessons is directly in profile
    if (userStats?.profile?.completedLessons) {
      completedLessons = userStats.profile.completedLessons;
    }
    // 2. Check if it's at the root level
    else if (userStats?.completedLessons) {
      completedLessons = userStats.completedLessons;
    }
    // 3. Extract from study sessions if available
    else if (userStats?.recentSessions && userStats.recentSessions.length > 0) {
      completedLessons = extractCompletedLessonsFromSessions(userStats.recentSessions);
    }
    // 4. Fetch from Firestore if we have a userId and no data yet
    else {
      const targetUserId = userId || auth.currentUser?.uid;
      if (targetUserId) {
        completedLessons = await fetchCompletedLessonsFromFirestore(targetUserId);
      }
    }
    
    return await calculateStudyPlanProgress(completedLessons);
  } catch (error) {
    console.error('Error getting study plan progress for dashboard:', error);
    return {
      completed: 0,
      total: 32,
      estimatedTime: 12
    };
  }
};
